import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMatch, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Icon from '@/components/Icon';
import LocationPicker from '@/components/LocationPicker';
import { applySuggestion, suggestFor } from '@/lib/suggestions';
import { readPlace, type Place } from '@/stores/locations';
import type { LocationRow } from '@/stores/admin';

/**
 * One sheet for everything that narrows the grid, plus the one thing that does not: the place.
 * It is the only modal pattern in the app, it opens from the bottom where the thumb already is,
 * and it writes its result to the URL so a view can be shared and reloaded.
 *
 * The place is two controls rather than one, because there are two ways to mean a place. The rail
 * cities are the handful worth a tap; anything else is typed, and the box under them reaches all
 * fifteen thousand localities. Picking either lands on the same kind of page.
 *
 * Radix owns the focus trap, the escape key and the scroll lock; the styling is ours.
 */
export default function FindSheet({
  open,
  onOpenChange,
  locations,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locations: LocationRow[];
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // A place page carries its place in the path, not the query: `/c/sydney` has no `?subject=`.
  // Without this the sheet opened from Sydney's own masthead said All Australia, and the next
  // search quietly dropped Sydney.
  const routePlace = useMatch('/c/:city')?.params.city ?? null;
  // Read when the sheet opens, not watched: the rail landing after the reader started typing
  // must not wipe what they typed.
  const locationsRef = useRef(locations);
  locationsRef.current = locations;

  const [query, setQuery] = useState('');
  const queryRef = useRef<HTMLInputElement>(null);
  const suggestions = useMemo(() => suggestFor(query), [query]);
  const [city, setCity] = useState<string | null>(null);
  // What the typed box has settled on, kept beside the chips so the sheet can show which of the
  // two the reader actually used.
  const [place, setPlace] = useState<Place | null>(null);
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');

  // The sheet is a snapshot of the current URL, taken each time it opens: a half-typed filter
  // that was abandoned should not come back on the next open.
  useEffect(() => {
    if (!open) return;
    setQuery(params.get('q') ?? '');
    setAgeMin(params.get('age_min') ?? '');
    setAgeMax(params.get('age_max') ?? '');

    const subject = params.get('subject') ?? routePlace;
    const onRail = locationsRef.current.some((location) => location.id === subject);
    setCity(onRail ? subject : null);
    // A subject that is not a rail city has to be fetched before the sheet can print its name.
    if (subject && !onRail) void readPlace(subject).then(setPlace);
    else setPlace(null);
  }, [open, params, routePlace]);

  function apply(event: FormEvent) {
    event.preventDefault();
    // The typed place wins over a chip: it is the more specific thing the reader said, and it is
    // the one they said last, because choosing it is what clears the chips.
    const subject = place?.id ?? city;

    const next = new URLSearchParams();
    if (query.trim()) next.set('q', query.trim());
    if (subject) next.set('subject', subject);
    // A range typed backwards is a range, not a request for nobody: 40 to 25 means 25 to 40.
    const [low, high] =
      ageMin && ageMax && Number(ageMin) > Number(ageMax) ? [ageMax, ageMin] : [ageMin, ageMax];
    if (low) next.set('age_min', low);
    if (high) next.set('age_max', high);
    // A rail city on its own has a page of its own, and that is the URL worth landing on: it is
    // the one somebody can send. A place typed into the box is a search, which is `/browse`: that
    // place's own advertisements, then the places within the search radius of it. Anything else is
    // a narrowing of the country, also `/browse`.
    const placeOnly = subject && !place && !query.trim() && !ageMin && !ageMax;
    if (placeOnly) navigate(`/c/${subject}`);
    else navigate(next.toString() ? `/browse?${next}` : '/browse');
    onOpenChange(false);
  }

  // Clearing is an answer, not a form reset: a reader who taps it and closes the sheet expects
  // the results to stop being narrowed, so it lands on the whole country there and then.
  function clear() {
    setQuery('');
    setCity(null);
    setPlace(null);
    setAgeMin('');
    setAgeMax('');
    navigate('/browse');
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ee-scrim" />
        <Dialog.Content className="ee-sheet" aria-describedby={undefined}>
          <form onSubmit={apply}>
            <div className="ee-sheet__head">
              <Dialog.Title className="ee-sheet__title">Find someone</Dialog.Title>
              <Dialog.Close
                className="ee-header__icon"
                aria-label="Close"
                data-ee-feature="find.close"
              >
                <Icon name="close" />
              </Dialog.Close>
            </div>

            <div className="ee-sheet__body">
              <label className="ee-group">
                <span className="ee-label">Words</span>
                <Input
                  ref={queryRef}
                  name="q"
                  autoComplete="off"
                  placeholder="Name, suburb, or what they offer"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  data-ee-feature="find.query"
                />
              </label>

              {/*
                The words the profiles themselves use, so a visitor who does not know what this
                site calls a thing is told before they have to guess. Chips rather than a
                dropdown floating over the sheet: the city row directly below is already this
                shape, and a list that opens over a bottom sheet has nowhere to open to.

                Every one of these is a term the index actually holds — they are built from the
                same vocabularies search reads — and the "I don't do" list is left out of both.
              */}
              {suggestions.length ? (
                <div className="ee-group" role="group" aria-label="Suggested words">
                  <p className="ee-label">Try</p>
                  <div className="ee-field-row">
                    {suggestions.map((label) => (
                      <button
                        key={label}
                        type="button"
                        className="ee-chip"
                        onClick={() => {
                          setQuery(applySuggestion(query, label));
                          // Focus returns to the box, because the next thing a visitor does is
                          // either type another word or press the one button below.
                          queryRef.current?.focus();
                        }}
                        data-ee-feature="find.suggestion"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="ee-group" role="group" aria-label="City">
                <p className="ee-label">City</p>
                <div className="ee-field-row">
                  <button
                    type="button"
                    className={`ee-chip${city === null && !place ? ' ee-chip--active' : ''}`}
                    aria-pressed={city === null && !place}
                    onClick={() => {
                      setCity(null);
                      setPlace(null);
                    }}
                    data-ee-feature="find.city"
                  >
                    All Australia
                  </button>
                  {locations.map((location) => (
                    <button
                      key={location.id}
                      type="button"
                      className={`ee-chip${city === location.id && !place ? ' ee-chip--active' : ''}`}
                      aria-pressed={city === location.id && !place}
                      onClick={() => {
                        setCity(location.id);
                        setPlace(null);
                      }}
                      data-ee-feature="find.city"
                    >
                      {location.name}
                    </button>
                  ))}
                </div>
              </div>

              {/*
                Anywhere else. The chips are the cities worth a tap; this reaches every suburb and
                town in the country, and picking one takes the chips out of it — a place page is
                about one place, and two answers to "where" is not a question the page can render.
              */}
              <LocationPicker
                label="Somewhere else"
                hint="A suburb, a town, or a postcode."
                feature="find.place"
                value={place}
                onChange={(next) => {
                  setPlace(next);
                  if (next) setCity(null);
                }}
              />

              <div className="ee-field-row">
                <label className="ee-group ee-field--short">
                  <span className="ee-label">Age from</span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={18}
                      max={99}
                      value={ageMin}
                      onChange={(event) => setAgeMin(event.target.value)}
                      data-ee-feature="find.age-min"
                    />
                  </label>
                <label className="ee-group ee-field--short">
                  <span className="ee-label">Age to</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={18}
                    max={99}
                    value={ageMax}
                    onChange={(event) => setAgeMax(event.target.value)}
                    data-ee-feature="find.age-max"
                  />
                </label>
              </div>
            </div>

            <div className="ee-sheet__foot">
              <Button type="submit" size="lg" variant="solid" data-ee-feature="find.apply">
                Show results
              </Button>
              <Button type="button" variant="ghost" onClick={clear} data-ee-feature="find.clear">
                Clear everything
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
